import {
  type ReferralBackfillImportResponse,
  ReferralBackfillImportResponse as ReferralBackfillImportResponseSchema,
  type ReferralBackfillPreviewResponse,
  ReferralBackfillPreviewResponse as ReferralBackfillPreviewResponseSchema,
  type ReferralBackfillRow,
} from '@uit/shared/schemas/referral-backfill';
import {
  type VendorPickerResponse,
  VendorPickerResponse as VendorPickerResponseSchema,
} from '@uit/shared/schemas/vendor';
import { useRef, useState } from 'react';
import { useApi } from '../../lib/api.js';
import s from './ReferralBackfillSection.module.scss';

// Referral backfill (T-110): load a referrer vendor's master name list (tab 2
// of Eric's per-vendor file, saved as a single-column CSV) onto existing
// engagements. Mockup: docs/mockups/referral_backfill_mockup.html.

const STATUS_LABEL: Record<ReferralBackfillRow['status'], string> = {
  matched: 'matched',
  already_set: 'already set',
  conflict: 'conflict',
  ambiguous: 'ambiguous',
  unmatched: 'unmatched',
  no_engagements: 'no engagements',
};

function outcome(row: ReferralBackfillRow, vendorName: string): string {
  switch (row.status) {
    case 'matched':
      return `Sets referrer on ${row.engagements_to_set} engagement${row.engagements_to_set === 1 ? '' : 's'}`;
    case 'already_set':
      return `All engagements already referred by ${vendorName} — no change`;
    case 'conflict':
      return `Already referred by ${row.conflict_vendors.join(', ')} — left untouched, review by hand`;
    case 'ambiguous':
      return `${row.resource_matches} resources share this name — assign manually on the resource page`;
    case 'unmatched':
      return 'No resource with this name — fix the CSV and re-upload';
    case 'no_engagements':
      return 'Resource has no engagements — nothing to set';
  }
}

function rowsLabel(row: ReferralBackfillRow): string {
  return row.row_numbers.length > 1 ? ` (rows ${row.row_numbers.join(' + ')})` : '';
}

function csvCell(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

function downloadIssueReport(preview: ReferralBackfillPreviewResponse): void {
  const issues = preview.rows.filter((r) => r.status !== 'matched' && r.status !== 'already_set');
  const lines = [
    'name,status,detail',
    ...issues.map((r) =>
      [csvCell(r.name), STATUS_LABEL[r.status], csvCell(outcome(r, preview.vendor.business_name))].join(','),
    ),
  ];
  const blob = new Blob([lines.join('\n') + '\n'], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'referral_backfill_issues.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function postUpload<T>(url: string, file: File, vendorId: string): Promise<T> {
  const fd = new FormData();
  fd.append('vendor_id', vendorId);
  fd.append('file', file);
  const r = await fetch(url, { method: 'POST', credentials: 'include', body: fd });
  if (!r.ok) throw new Error(`${r.status}`);
  return (await r.json()) as T;
}

export function ReferralBackfillSection() {
  const { data: referrers } = useApi<VendorPickerResponse>(
    '/api/vendors?referrer=true&page_size=100',
    VendorPickerResponseSchema,
  );
  const [vendorId, setVendorId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ReferralBackfillPreviewResponse | null>(null);
  const [result, setResult] = useState<
    (ReferralBackfillImportResponse & { vendor_name: string }) | null
  >(null);

  function reset(): void {
    setPreview(null);
    setResult(null);
    setError(null);
  }

  async function handlePreview(): Promise<void> {
    if (!file || !vendorId) return;
    setBusy(true);
    reset();
    try {
      const p = await postUpload<unknown>('/api/backfill/referrals/preview', file, vendorId);
      setPreview(ReferralBackfillPreviewResponseSchema.parse(p));
    } catch (err) {
      setError(`Preview failed (${err instanceof Error ? err.message : String(err)}).`);
    } finally {
      setBusy(false);
    }
  }

  async function handleImport(): Promise<void> {
    if (!file || !vendorId || !preview) return;
    const vendorName = preview.vendor.business_name;
    setBusy(true);
    setError(null);
    try {
      const r = await postUpload<unknown>('/api/backfill/referrals/import', file, vendorId);
      setResult({ ...ReferralBackfillImportResponseSchema.parse(r), vendor_name: vendorName });
      setPreview(null);
      setFile(null);
      setVendorId('');
      // Clear the native input too — otherwise it still shows the old filename
      // and re-choosing the SAME file fires no change event, leaving Preview disabled.
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(`Import failed (${err instanceof Error ? err.message : String(err)}).`);
    } finally {
      setBusy(false);
    }
  }

  const issueCount = preview
    ? preview.conflicts + preview.ambiguous + preview.unmatched + preview.no_engagements
    : 0;

  return (
    <>
      <section className={s.section}>
        <h2 className={s.sectionTitle}>Referral backfill</h2>
        <p className={s.sectionSub}>
          Load an existing referrer&rsquo;s name list onto current engagements. One CSV per referrer
          vendor — a single column of <code>Last, First</code> names (in Excel: open the vendor file
          → tab 2 → Save As CSV). Header row optional. Matched names get the referrer set on every
          engagement that doesn&rsquo;t have one yet; nothing is ever overwritten.
        </p>

        <div className={s.controls}>
          <div className={s.field}>
            <label className={s.label} htmlFor="ref-vendor">
              Referrer vendor
            </label>
            <select
              id="ref-vendor"
              className={s.select}
              value={vendorId}
              onChange={(e) => {
                setVendorId(e.target.value);
                reset();
              }}
            >
              <option value="">— Select referrer —</option>
              {referrers?.items.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.business_name}
                </option>
              ))}
            </select>
            <div className={s.hint}>Referrer vendors only (managed in Settings).</div>
          </div>

          <div className={s.field}>
            <label className={s.label} htmlFor="ref-file">
              Name list (CSV)
            </label>
            <input
              id="ref-file"
              type="file"
              accept=".csv,text/csv"
              ref={fileInputRef}
              className={s.fileInput}
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                reset();
              }}
            />
            <div className={s.hint}>
              Single column of <code>Last, First</code> names.
            </div>
          </div>

          <div className={s.field}>
            <span className={s.labelSpacer} aria-hidden="true" />
            <button
              type="button"
              className={s.previewBtn}
              aria-label="Preview referral backfill"
              disabled={!file || vendorId === '' || busy}
              onClick={() => void handlePreview()}
            >
              {busy ? 'Working…' : 'Preview'}
            </button>
          </div>
        </div>

        {error && (
          <div className={s.error} role="alert">
            {error}
          </div>
        )}

        {result && (
          <div className={s.info} role="status">
            Referrer set on {result.engagements_set} engagement
            {result.engagements_set === 1 ? '' : 's'} across {result.resources_affected} resource
            {result.resources_affected === 1 ? '' : 's'} ({result.vendor_name}).
            {result.rows_skipped > 0 &&
              ` ${result.rows_skipped} row${result.rows_skipped === 1 ? '' : 's'} skipped — fix and re-upload; re-running is safe.`}
          </div>
        )}
      </section>

      {preview && (
        <section className={s.section}>
          <h2 className={s.sectionTitle}>Preview — {preview.vendor.business_name}</h2>
          <p className={s.sectionSub}>
            Nothing is written yet. Fix unmatched/ambiguous names in the CSV and re-upload, or
            import the matched rows now — re-running later is safe (already-set rows are skipped).
          </p>

          <div className={s.chips}>
            <span className={s.chip}>
              <strong>{preview.rows_in}</strong> rows in
            </span>
            <span className={s.chip}>
              <strong>{preview.unique_names}</strong> unique names
            </span>
            <span className={`${s.chip} ${s.chipOk}`}>
              <strong>{preview.matched}</strong> matched · will set{' '}
              <strong>{preview.engagements_to_set}</strong> engagements
            </span>
            <span className={s.chip}>
              <strong>{preview.already_set}</strong> already set
            </span>
            {preview.conflicts > 0 && (
              <span className={`${s.chip} ${s.chipBad}`}>
                <strong>{preview.conflicts}</strong> conflicts
              </span>
            )}
            {preview.ambiguous > 0 && (
              <span className={`${s.chip} ${s.chipWarn}`}>
                <strong>{preview.ambiguous}</strong> ambiguous
              </span>
            )}
            {preview.unmatched > 0 && (
              <span className={`${s.chip} ${s.chipWarn}`}>
                <strong>{preview.unmatched}</strong> unmatched
              </span>
            )}
            {preview.no_engagements > 0 && (
              <span className={`${s.chip} ${s.chipWarn}`}>
                <strong>{preview.no_engagements}</strong> without engagements
              </span>
            )}
          </div>

          <table className={s.table}>
            <thead>
              <tr>
                <th scope="col">Name (as uploaded)</th>
                <th scope="col">Status</th>
                <th scope="col">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((r) => (
                <tr key={r.name}>
                  <td>
                    <span className={s.name}>{r.name}</span>
                    <span className={s.rowsNote}>{rowsLabel(r)}</span>
                  </td>
                  <td>
                    <span className={`${s.status} ${s[`status_${r.status}`] ?? ''}`}>
                      {STATUS_LABEL[r.status]}
                    </span>
                  </td>
                  <td>{outcome(r, preview.vendor.business_name)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={s.footerBar}>
            <span className={s.footerText}>
              <strong>{preview.engagements_to_set}</strong> engagements across{' '}
              <strong>{preview.matched}</strong> resources will get{' '}
              <strong>{preview.vendor.business_name}</strong> as referrer.
            </span>
            <span className={s.footerActions}>
              {issueCount > 0 && (
                <button
                  type="button"
                  className={s.secondaryBtn}
                  onClick={() => downloadIssueReport(preview)}
                >
                  Download issue report
                </button>
              )}
              <button
                type="button"
                className={s.importBtn}
                disabled={preview.matched === 0 || busy}
                onClick={() => void handleImport()}
              >
                {busy ? 'Working…' : `Import ${preview.matched} matched`}
              </button>
            </span>
          </div>
        </section>
      )}
    </>
  );
}
